/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useEffect, useRef, useState } from 'react';

import Link from 'next/link';

import CustomIcon from '@components/utils/CustomIcon';
import { CloseIcon, RobotIcon, SendPlane } from '@assets/icons';
import Typography from '@components/Typography';
import Button from '@components/Button/next';
import { useInputApi } from '@components/Input';
import Markdown from '@components/utils/Markdown';
import { logChatgptEvent } from '@utils/gtm/chatgpt-gtm';

import {
  AssistantMessage,
  AssistantMessageLoading,
  ChatGPTModalContent,
  Container,
  Footer,
  Header,
  Main,
  SendQuestionButton,
  UserMessage,
  UserQuestionInput,
} from './styled';
import LoadingDots from './Loading';
import useChatApi from './useChatApi';
import SurveyModal from './SurveyModal';

interface Props {
  isChatGPTModalOpen: boolean;
  toggleChatGPTModal: () => void;
  setControl?: (boolean) => void;
}

type Message = {
  text: string;
  sender: 'user' | 'assistant';
};

const staticMessages = [
  {
    text: `Merhaba, Setur'un yapay zeka destekli Seyahat Asistanı'na hoş geldiniz! Size nasıl yardımcı olabilirim?`,
    sender: 'assistant',
  },
  {
    text: `Setur <strong>yaz fırsatlarıyla</strong> tatilini %50'ye varan indirimlerle, uygun fiyatlarla planlamanın tam zamanı. Üstelik YapıKredi World’e özel vade farksız 9 taksit imkanıyla. Kampanyalı oteller için sayfamızı ziyaret etmeyi unutma😊 <a href="https://www.setur.com.tr/yaz-firsatlari?kaynak=chatGPT"
    target='_blank'
    rel='noopener noreferrer'
    ><strong>Yaz Fırsatları</strong></a>`,
    sender: 'assistant',
  },
];

const ChatGPTModal = ({
  isChatGPTModalOpen,
  toggleChatGPTModal,
  setControl,
}: Props) => {
  const [userQuestion, setUserQuestion] = useState('');
  const [isSurveyOpen, setIsSurveyOpen] = useState(false);
  const [isSurveyClick, setIsSurveyClick] = useState(false);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);

  const { handleChat, isTyping, messages, setMessages } = useChatApi();
  const { inputApiRef, getInputApi } = useInputApi();

  // =====================================
  // Handle sending question to the assistant
  // =====================================
  const handleSendQuestion = async () => {
    if (userQuestion.length < 1) return;
    setMessages((prev) => [
      ...prev,
      { text: userQuestion?.trim(), sender: 'user' },
    ]);
    const userInputMessage: Message = {
      text: userQuestion?.trim(),
      sender: 'user',
    };
    let updatedMessages: Message[] = [];

    if (messages.length === 0) {
      updatedMessages = [userInputMessage];
    } else {
      updatedMessages = [...messages, userInputMessage];
    }

    setUserQuestion('');
    inputApiRef.current?.reset();

    await handleChat(updatedMessages);
  };

  // =====================================
  // Handle scroll to the last message
  // =====================================
  const scrollToBottom = () => {
    if (mainRef.current) {
      mainRef.current.scrollTo({
        top: mainRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  // =====================================
  // Handling fill the message from the session Storage
  // =====================================
  useEffect(() => {
    const storedMessages = sessionStorage.getItem('chatGPTMessages');
    if (storedMessages) {
      setMessages(JSON.parse(storedMessages));
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem('chatGPTMessages', JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    const scroll = () => {
      scrollToBottom();
    };

    const timeout = setTimeout(scroll, 100);
    return () => clearTimeout(timeout);
  }, [messages]);

  const addQueryParamToLinks = (markdown: string) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

    const replaceFunction = (match, text, url) => {
      const modifiedUrl = new URL(url);
      modifiedUrl.searchParams.append('kaynak', 'chatgpt');
      return `[${text}](${modifiedUrl.toString()})`;
    };

    return markdown.replace(linkRegex, replaceFunction) || markdown;
  };
  return (
    <Container
      visible={isChatGPTModalOpen}
      onClick={() => {
        toggleChatGPTModal();
        setControl?.(false);
      }}
    >
      <ChatGPTModalContent
        visible={isChatGPTModalOpen}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <Typography variant='Title4' weight='Bold' color='D400'>
            Setur Seyahat Asistanı
          </Typography>
          <Button
            variant='Text'
            onClick={(e) => {
              if (isSurveyClick) {
                e.stopPropagation();
                setControl?.(false);
                sessionStorage.removeItem('chatGPTMessages');
                toggleChatGPTModal();
              } else setIsSurveyOpen(true);
            }}
          >
            <CustomIcon icon={CloseIcon} fontSize={24} paletteColor='D400' />
          </Button>
        </Header>
        {isSurveyModalOpen ? (
          <iframe
            src='https://psn.vc/0tl6ajggve'
            title='Survey Modal'
            width='100%'
            height='100%'
            style={{ border: 'none' }}
            allowFullScreen
          />
        ) : (
          <>
            <Main ref={mainRef}>
              {[...staticMessages, ...messages].map((message) => {
                return message.sender === 'user' ? (
                  <UserMessage>
                    <Typography
                      variant='caption2'
                      weight='Regular'
                      color='D400'
                    >
                      {message.text}
                    </Typography>
                  </UserMessage>
                ) : (
                  <AssistantMessage>
                    <CustomIcon icon={RobotIcon} fontSize={40} />
                    <Typography
                      variant='caption2'
                      weight='Regular'
                      color='NL0'
                      as='p'
                    >
                      <Markdown
                        text={addQueryParamToLinks(message.text || ' ')}
                        components={{
                          a: ({ node, ...props }) => (
                            <Link
                              prefetch={false}
                              onClick={() => {
                                if (props?.href) {
                                  setControl?.(false);
                                  logChatgptEvent(
                                    new URL(props?.href).pathname
                                  );
                                }
                              }}
                              {...(props as any)}
                            />
                          ),
                        }}
                      />
                    </Typography>
                  </AssistantMessage>
                );
              })}

              {isTyping ? (
                <AssistantMessageLoading>
                  <CustomIcon icon={RobotIcon} fontSize={40} />
                  <LoadingDots />
                </AssistantMessageLoading>
              ) : null}
            </Main>

            <Footer>
              <UserQuestionInput
                useNative
                placeholder='Benim için ne yapabilirsin ?'
                value={userQuestion}
                onChange={(e) => setUserQuestion(e)}
                getInputAPI={getInputApi}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    if (userQuestion?.length > 0 && !isTyping) {
                      handleSendQuestion();
                    }
                  }
                }}
              />
              <SendQuestionButton
                variant='Primary'
                disabled={userQuestion?.length < 1 || isTyping}
                onClick={handleSendQuestion}
              >
                <CustomIcon icon={SendPlane} fontSize={16} paletteColor='NL0' />
              </SendQuestionButton>
            </Footer>
          </>
        )}
      </ChatGPTModalContent>

      {isSurveyOpen && (
        <SurveyModal
          setControl={setControl}
          toggleChatGPTModal={toggleChatGPTModal}
          setIsSurveyOpen={setIsSurveyOpen}
          setIsSurveyModalOpen={setIsSurveyModalOpen}
          setIsSurveyClick={setIsSurveyClick}
        />
      )}
    </Container>
  );
};

export default ChatGPTModal;
